import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RdwMonthRow {
  datum: string;
  // Totaal N2+N3
  ev_trucks_nl_nieuw_maand: number;
  ev_trucks_nl_cumulatief: number;
  // Per categorie
  ev_n2_nieuw: number;
  ev_n2_cumulatief: number;
  ev_n3_nieuw: number;
  ev_n3_cumulatief: number;
  ev_m2_nieuw: number;
  ev_m2_cumulatief: number;
  ev_m3_nieuw: number;
  ev_m3_cumulatief: number;
  bron_registraties: string | null;
}

export interface RdwYearRow {
  jaar: number;
  nieuw_totaal: number;
  cumulatief_totaal: number;
  n2_nieuw: number;
  n2_cumulatief: number;
  n3_nieuw: number;
  n3_cumulatief: number;
  m2_nieuw: number;
  m2_cumulatief: number;
  m3_nieuw: number;
  m3_cumulatief: number;
  bron: string;
}

export function useLaadpleinMarktdata() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['laadplein-marktdata'],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('laadplein_marktdata')
        .select('datum, ev_trucks_nl_nieuw_maand, ev_trucks_nl_cumulatief, ev_n2_nieuw, ev_n2_cumulatief, ev_n3_nieuw, ev_n3_cumulatief, ev_m2_nieuw, ev_m2_cumulatief, ev_m3_nieuw, ev_m3_cumulatief, bron_registraties')
        .order('datum', { ascending: true });

      if (error) throw error;

      // Only keep rows with actual RDW data (bron starts with 'RDW')
      const rdwRows: RdwMonthRow[] = (rows ?? [])
        .filter((r: any) => r.bron_registraties && r.bron_registraties.startsWith('RDW'))
        .map((r: any) => ({
          datum: r.datum,
          ev_trucks_nl_nieuw_maand: r.ev_trucks_nl_nieuw_maand ?? 0,
          ev_trucks_nl_cumulatief: r.ev_trucks_nl_cumulatief ?? 0,
          ev_n2_nieuw: r.ev_n2_nieuw ?? 0,
          ev_n2_cumulatief: r.ev_n2_cumulatief ?? 0,
          ev_n3_nieuw: r.ev_n3_nieuw ?? 0,
          ev_n3_cumulatief: r.ev_n3_cumulatief ?? 0,
          ev_m2_nieuw: r.ev_m2_nieuw ?? 0,
          ev_m2_cumulatief: r.ev_m2_cumulatief ?? 0,
          ev_m3_nieuw: r.ev_m3_nieuw ?? 0,
          ev_m3_cumulatief: r.ev_m3_cumulatief ?? 0,
          bron_registraties: r.bron_registraties,
        }));

      // Aggregate by year for the chart
      const byYear: Record<number, RdwYearRow> = {};
      for (const row of rdwRows) {
        const year = new Date(row.datum).getFullYear();
        if (!byYear[year]) {
          byYear[year] = {
            jaar: year, nieuw_totaal: 0, cumulatief_totaal: 0,
            n2_nieuw: 0, n2_cumulatief: 0, n3_nieuw: 0, n3_cumulatief: 0,
            m2_nieuw: 0, m2_cumulatief: 0, m3_nieuw: 0, m3_cumulatief: 0,
            bron: 'RDW',
          };
        }
        const y = byYear[year];
        y.nieuw_totaal += (row.ev_n2_nieuw + row.ev_n3_nieuw + row.ev_m2_nieuw + row.ev_m3_nieuw) || row.ev_trucks_nl_nieuw_maand;
        y.n2_nieuw += row.ev_n2_nieuw;
        y.n3_nieuw += row.ev_n3_nieuw;
        y.m2_nieuw += row.ev_m2_nieuw;
        y.m3_nieuw += row.ev_m3_nieuw;
        // Cumulative: take the max (last month of the year)
        if (row.ev_trucks_nl_cumulatief > y.cumulatief_totaal) y.cumulatief_totaal = row.ev_trucks_nl_cumulatief;
        if (row.ev_n2_cumulatief > y.n2_cumulatief) y.n2_cumulatief = row.ev_n2_cumulatief;
        if (row.ev_n3_cumulatief > y.n3_cumulatief) y.n3_cumulatief = row.ev_n3_cumulatief;
        if (row.ev_m2_cumulatief > y.m2_cumulatief) y.m2_cumulatief = row.ev_m2_cumulatief;
        if (row.ev_m3_cumulatief > y.m3_cumulatief) y.m3_cumulatief = row.ev_m3_cumulatief;
      }

      const yearRows = Object.values(byYear).sort((a, b) => a.jaar - b.jaar);

      return {
        monthRows: rdwRows,
        yearRows,
        isLive: rdwRows.length > 0,
        latestMonth: rdwRows.length > 0 ? rdwRows[rdwRows.length - 1] : null,
      };
    },
    refetchInterval: 5 * 60_000,
  });

  const refresh = useMutation({
    mutationFn: async (category?: string) => {
      const body = category ? { category } : { category: 'N2' };
      const { error } = await supabase.functions.invoke('laadplein-rdw-ev-trucks', { body });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['laadplein-marktdata'] });
      queryClient.invalidateQueries({ queryKey: ['laadplein-sync-status'] });
    },
  });

  return {
    monthRows: data?.monthRows ?? [],
    yearRows: data?.yearRows ?? [],
    latestMonth: data?.latestMonth ?? null,
    isLive: data?.isLive ?? false,
    isLoading,
    refresh,
  };
}
